import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../lib/adminApi';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Search, Sparkles, Loader2, ExternalLink, Trash2 } from 'lucide-react';

const PAGE_SIZE = 20;

const AdminRequirementsPanel = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [analyzing, setAnalyzing] = useState(null); // id being analyzed
  const [result, setResult] = useState(null); // {req, ai}
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.requirements();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({ title: 'Failed to load requirements', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(needle));
  }, [rows, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    setSelected((prev) => {
      const pageIds = pagedRows.map((r) => r.id);
      const allSel = pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSel) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const allPageSelected = pagedRows.length > 0 && pagedRows.every((r) => selected.has(r.id));

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected requirement(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await adminApi.bulkDelete('requirements', ids);
      toast({ title: 'Bulk delete complete', description: `${r.deleted} requirement(s) deleted.` });
      setSelected(new Set());
      await load();
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setDeleting(false); }
  };

  const analyze = async (req) => {
    setAnalyzing(req.id);
    setResult({ req, ai: null });
    try {
      const ai = await adminApi.aiAnalyzeRequirement(req.id);
      setResult({ req, ai });
    } catch (e) {
      toast({ title: 'AI analyse failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      setResult(null);
    } finally { setAnalyzing(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Requirements</h1>
          <p className="text-sm text-gray-600">{filtered.length} record{filtered.length !== 1 ? 's' : ''} · Click <Sparkles className="w-3 h-3 inline" /> AI Analyze to get a summary + top vendor picks.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {selected.size > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={bulkDelete}
              disabled={deleting}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="requirements-bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> {deleting ? 'Deleting…' : `Delete ${selected.size} selected`}
            </Button>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search..." className="pl-8 w-64" data-testid="requirements-search" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No requirements yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={togglePage}
                      aria-label="Select all on page"
                      data-testid="requirements-select-all"
                    />
                  </th>
                  {['Name', 'Mobile', 'Title', 'Category', 'Created', 'Message'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">{h}</th>
                  ))}
                  <th className="px-4 py-2 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r) => (
                  <tr key={r.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selected.has(r.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleRow(r.id)}
                        aria-label="Select row"
                        data-testid={`requirements-select-${r.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.name || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.mobile || '-'}</td>
                    <td className="px-4 py-2 max-w-xs truncate">{r.title || r.product || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.category || '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-500">{r.created_at ? String(r.created_at).slice(0, 10) : '-'}</td>
                    <td className="px-4 py-2 max-w-md truncate">{r.message || '-'}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => analyze(r)}
                        disabled={analyzing === r.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-50 text-[#ee5e23] hover:bg-orange-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Analyse with AI"
                        data-testid={`req-ai-analyze-${r.id}`}
                      >
                        {analyzing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        AI Analyze
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs">
            <div>Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
              <span className="px-2">{currentPage} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="req-ai-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#ee5e23]" /> AI Analysis</DialogTitle>
            <DialogDescription>
              {result?.req ? <>Requirement: <b>{result.req.title || result.req.product || '-'}</b> from {result.req.name || '(unnamed buyer)'} · {result.req.mobile || '-'}</> : ''}
            </DialogDescription>
          </DialogHeader>
          {!result?.ai ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analysing…
            </div>
          ) : (
            <div className="space-y-5 text-sm">
              <div>
                <h4 className="font-semibold text-gray-700 mb-1">Summary</h4>
                <p className="text-gray-800 leading-relaxed" data-testid="req-ai-summary">{result.ai.summary || '(no summary returned)'}</p>
              </div>
              {Array.isArray(result.ai.top_reasons) && result.ai.top_reasons.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-1">Why these vendors</h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    {result.ai.top_reasons.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Top matched vendors <span className="text-xs font-normal text-gray-500">({result.ai.candidate_count || 0} candidate{(result.ai.candidate_count || 0) !== 1 ? 's' : ''} scanned)</span>
                </h4>
                {(result.ai.top_vendors || []).length === 0 ? (
                  <div className="text-gray-500 text-xs">No active vendors matched the requirement&apos;s category/keyword.</div>
                ) : (
                  <ul className="space-y-2">
                    {result.ai.top_vendors.map((v) => (
                      <li key={v.id} className="border border-gray-200 rounded-md p-2 flex items-center justify-between">
                        <div>
                          <div className="font-medium">{v.name}</div>
                          <div className="text-[11px] text-gray-500">{v.city || 'City N/A'} · {v.plan_name || 'No plan'} · rating {v.rating ?? '-'}</div>
                        </div>
                        <a href={`/vendor/${v.slug || v.id}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#2c54b2] hover:underline">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex justify-end pt-2 border-t">
                <Button variant="outline" onClick={() => setResult(null)} data-testid="req-ai-close">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRequirementsPanel;
